import { useSelector } from "react-redux";
import { selector as groupApi } from "../deviceManagement/groupSlice";
import { userRoles, IFlatUser, IRole } from "./util";
import { IListUser, IUserRole } from "../../common/types/userTypes";
import { IGroup } from "../../common/types/groupTypes";

const useUserGroup = () => {
  const groups: IGroup[] = useSelector(groupApi).groups;
  const groupIdToHierarchyMap: Record<string, string> = useSelector(groupApi).groupIdToHierarchyMap;
  const NOT_AVAILABLE: string = "Not available";

  const updateUsersWithGroup = (
    userWithRoleNames: IFlatUser[],
    groups: IGroup[]
  ): IFlatUser[] => {
    const data = userWithRoleNames.map((user) => {
      const group: IGroup | undefined = groups.find(
        (group) => group.orgId == user.groupId
      );
      return {
        ...user,
        groupId: group === undefined ? user.parents : group.displayName,
      };
    });
    return data;
  };

  const updateUsersWithRoleNames = (
    updatedData: IFlatUser[],
    userRoles: IRole[]
  ) => {
    return updatedData?.map((item: IFlatUser) => {
      const matchingRole = userRoles.find(
        (role: IRole) => role.id === item.roleId
      );
      return {
        ...item,
        roleId: matchingRole ? matchingRole.displayName : item.roleId,
      };
    });
  };

  const updateUsersWithOrganization = (
    flattenedData: IFlatUser[],
    groupIdToHierarchyMap: Record<string, string>
  ): IFlatUser[] => {
    const organizationKeys: string[] = Object.keys(groupIdToHierarchyMap);
    return flattenedData.map((user: IFlatUser) => {
      const isOrgPresent: boolean = organizationKeys.includes(
        user.organization
      );
      let parentOrg: string = isOrgPresent
        ? groupIdToHierarchyMap[user.organization]
        : NOT_AVAILABLE;
      return {
        ...user,
        parents: parentOrg,
      };
    });
  };

  const flattenUserData = (usersList: IListUser[]): IFlatUser[] =>
    usersList?.flatMap((user: IListUser) =>
      user.roles.map((role: IUserRole) => ({
        email: user.email,
        name: user.name,
        phoneNo: user.phoneNo,
        organization: role.organizationId,
        groupId: role.groupId,
        groupName: role?.groupId ? role.groupId : role.organizationId,
        roleId: role.role,
        _id: role._id,
        userId: user.userId,
        status: user.status,
        isPasswordEnable: user.isPasswordEnable,
      }))
    );

  const formatUserData = (usersList: IListUser[]): IFlatUser[] => {
    const flattenedData: IFlatUser[] = flattenUserData(usersList);
    const usersWithOrganization: IFlatUser[] = updateUsersWithOrganization(
      flattenedData,
      groupIdToHierarchyMap
    );
    const userWithRoleNames: IFlatUser[] = updateUsersWithRoleNames(
      usersWithOrganization,
      userRoles
    );
    const usersWithGroups: IFlatUser[] = updateUsersWithGroup(userWithRoleNames, groups);
    return usersWithGroups;
  };

  return {
    formatUserData,
  };
};

export default useUserGroup;